import { motion, Transition, Variants } from "framer-motion";
import { ReactNode, useState, useEffect } from "react";

interface PageTransitionProps {
  children: ReactNode;
}

// No animation variants for reduced motion
const noAnimationVariants: Variants = {
  initial: { opacity: 1 },
  animate: { opacity: 1 },
  exit: { opacity: 1 },
};

// Simpler variants for mobile - no scale transforms
const mobileVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const desktopVariants: Variants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
};

const instantTransition: Transition = {
  duration: 0,
};

const mobileTransition: Transition = {
  type: "tween",
  ease: "easeOut",
  duration: 0.15,
};

const desktopTransition: Transition = {
  type: "tween",
  ease: [0.25, 0.46, 0.45, 0.94],
  duration: 0.25,
};

export const PageTransition = ({ children }: PageTransitionProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    const checkMotion = () => {
      setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    };
    
    checkMobile();
    checkMotion();
  }, []);

  // Determine variants and transition based on device and preferences
  const variants = prefersReducedMotion 
    ? noAnimationVariants 
    : isMobile 
      ? mobileVariants 
      : desktopVariants;
      
  const transition = prefersReducedMotion 
    ? instantTransition 
    : isMobile 
      ? mobileTransition 
      : desktopTransition;

  return (
    <motion.div
      initial="initial"
      animate="animate"
      exit="exit"
      variants={variants}
      transition={transition}
      className="min-h-screen"
    >
      {children}
    </motion.div>
  );
};
